import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { NotificationProvider } from './context/NotificationContext';
import { SwapProvider } from './context/SwapContext';
import { OrderProvider } from './context/OrderContext';
import NotificationToast from './components/NotificationToast';
import { getStoredDarkMode, setStoredDarkMode, onThemeChange } from './utils/theme';

// Import components
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import CategoriesSection from './components/CategoriesSection';
import TrendingSection from './components/TrendingSection';
import Homepage from './components/Homepage';
import Shop from './components/Shop';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import SwapDeals from './components/SwapDeals';
import StoreProfile from './components/StoreProfile';
import OrderConfirmation from './components/OrderConfirmation';
import Footer from './components/Footer';
import FloatingButtons from './components/FloatingButtons';
import BackToTop from './components/BackToTop';
import LoginSignup from './components/LoginSignup';
import ProductDetails from './components/ProductDetails';
import BecomeSeller from './components/BecomeSeller';
import SellerSignup from './components/SellerSignup';
import SellerLogin from './components/SellerLogin';
import VerifyEmail from './components/VerifyEmail';
import ResetPassword from './components/ResetPassword';
import SellerDashboard from './components/SellerDashboard';
import PremiumSellerDashboard from './components/PremiumSellerDashboard';
import BuyerDashboard from './components/BuyerDashboard';
import PremiumBuyerDashboard from './components/PremiumBuyerDashboard';
import AdminDashboard from './components/AdminDashboard';
import PaymentVerification from './components/PaymentVerification';
import ProtectedRoute from './components/ProtectedRoute';
import HelpCenter from './components/HelpCenter';
import Disputes from './components/Disputes';
import SupportChat from './components/SupportChat';
import OrderTracking from './components/OrderTracking';
import Wishlist from './components/Wishlist';
import SavedSearches from './components/SavedSearches';
import Notifications from './components/Notifications';
import StayConnectedPopup from './components/StayConnectedPopup';
import KYCVerification from './components/KYCVerification';
import OrderDetails from './components/OrderDetails';
import OrdersList from './components/OrdersList';
import BankAccountsManager from './components/BankAccountsManager';
import AdminSetup from './components/AdminSetup';
import NotFound from './components/NotFound';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import RefundPolicy from './components/RefundPolicy';
import AcceptableUsePolicy from './components/AcceptableUsePolicy';


// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Keeps the storefront `dark-mode` body class correct on every navigation.
// Dashboards use their own `AutoboyExpress-dash-dark-mode`/`tw-dark-mode` toggle,
// so we strip the storefront class once on entering a dashboard route to avoid
// style bleed, and (re)apply it on storefront routes — which is what fixes the
// toggle desync when returning home.
function ThemeSync({ darkMode }) {
  const location = useLocation();
  const isDashboard = /dashboard/i.test(location.pathname);

  // Strip storefront classes on route entry only. This must NOT depend on
  // `darkMode` — a dashboard's own toggle calls setStoredDarkMode(), which
  // dispatches the same-tab theme-change event this component listens to
  // (see App's darkMode state below), so `darkMode` changes on every dashboard
  // toggle too. If this effect depended on it, re-firing here would strip
  // `tw-dark-mode` right back off immediately after a dashboard adds it,
  // making the dashboard's dark toggle a permanent no-op.
  useEffect(() => {
    if (isDashboard) {
      document.body.classList.remove('dark-mode');
      document.body.classList.remove('tw-dark-mode');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Sync the storefront's own class with the shared toggle — but only while
  // actually on a storefront route; dashboards manage this class themselves.
  useEffect(() => {
    if (!isDashboard) {
      document.body.classList.toggle('dark-mode', darkMode);
      // Tailwind's `prefix` option also prefixes the darkMode selector class
      // itself (see tailwind.config.js), so storefront pages using tw- dark:
      // variants (e.g. Checkout.jsx) key off this class, not the plain one.
      document.body.classList.toggle('tw-dark-mode', darkMode);
    }
  }, [darkMode, isDashboard]);
  return null;
}

function App() {
  const [darkMode, setDarkMode] = useState(() => getStoredDarkMode());

  // Re-sync whenever the shared preference changes anywhere (a dashboard toggle,
  // another tab, etc.) so the homepage toggle never drifts out of sync.
  useEffect(() => {
    const unsubscribe = onThemeChange((isDark) => setDarkMode(isDark));

    // Follow OS preference only while the user hasn't explicitly chosen a theme.
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e) => {
      if (!localStorage.getItem('theme')) setDarkMode(e.matches);
    };
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      unsubscribe();
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  const toggleTheme = () => {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    setStoredDarkMode(newTheme);
  };

  // Landing Page Layout (original design)
  const LandingPage = () => (
    <>
      <HeroSection />
      <TrendingSection />
      <CategoriesSection />
      
    </>
  );

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || "PASTE_YOUR_GOOGLE_CLIENT_ID_HERE"}>
      <AuthProvider>
        <CartProvider>
          <NotificationProvider>
            <SwapProvider>
            <OrderProvider>
            <Router>
              <ThemeSync darkMode={darkMode} />
              <div className="App">
              <NotificationToast />
              <StayConnectedPopup />
              <Routes>
            {/* Authentication routes without navbar/footer */}
            <Route path="/login" element={<LoginSignup />} />
            <Route path="/register" element={<LoginSignup />} />
            <Route path="/signup" element={<LoginSignup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* Target of the link in the password-reset email */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth" element={<LoginSignup />} />
            <Route path="/setup-initial-admin" element={<AdminSetup />} />

            {/* Seller auth routes without navbar/footer - immersive full-bleed layout */}
            <Route path="/seller-signup" element={<SellerSignup />} />
            <Route path="/seller-signin" element={<SellerLogin />} />
            <Route path="/seller-login" element={<SellerLogin />} />

            {/* Dashboard routes without navbar/footer - PROTECTED */}
            <Route path="/seller-dashboard" element={
              <ProtectedRoute requiredUserType="seller" requiresPremium={false}>
                <SellerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/buyer-dashboard" element={
              <ProtectedRoute requiredUserType="buyer" requiresPremium={false}>
                <BuyerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/premium-seller-dashboard" element={
              <ProtectedRoute requiredUserType="seller" requiresPremium={true}>
                <PremiumSellerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/premium-buyer-dashboard" element={
              <ProtectedRoute requiredUserType="buyer" requiresPremium={true}>
                <PremiumBuyerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin-dashboard" element={
              <ProtectedRoute requiredUserType="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } />

            {/* Payment Verification Route */}
            <Route path="/premium/verify" element={<PaymentVerification />} />

            {/* Routes with navbar and footer */}
            <Route path="/*" element={
              <>
                <Navbar darkMode={darkMode} />
                <FloatingButtons darkMode={darkMode} toggleTheme={toggleTheme} />

                <main>
                  <Routes>
                    {/* Landing page with original design */}
                    <Route path="/" element={<LandingPage />} />
                    
                    {/* Homepage with products showcase - PUBLIC (browsing allowed, actions gated) */}
                    <Route path="/homepage" element={<Homepage />} />

                    {/* Shop - PUBLIC (browsing allowed, add-to-cart/checkout gated) */}
                    <Route path="/shop" element={<Shop darkMode={darkMode} />} />
                    <Route path="/cart" element={
                      <ProtectedRoute>
                        <Cart />
                      </ProtectedRoute>
                    } />
                    <Route path="/wishlist" element={
                      <ProtectedRoute>
                        <Wishlist />
                      </ProtectedRoute>
                    } />
                    <Route path="/saved-searches" element={
                      <ProtectedRoute>
                        <SavedSearches />
                      </ProtectedRoute>
                    } />
                    <Route path="/notifications" element={
                      <ProtectedRoute>
                        <Notifications />
                      </ProtectedRoute>
                    } />
                    <Route path="/checkout" element={
                      <ProtectedRoute>
                        <Checkout />
                      </ProtectedRoute>
                    } />
                    <Route path="/swap-deals" element={
                      <ProtectedRoute>
                        <SwapDeals />
                      </ProtectedRoute>
                    } />
                    <Route path="/store/:userId" element={
                      <ProtectedRoute>
                        <StoreProfile />
                      </ProtectedRoute>
                    } />
                    <Route path="/order-confirmation/:orderId" element={
                      <ProtectedRoute>
                        <OrderConfirmation />
                      </ProtectedRoute>
                    } />
                    <Route path="/orders" element={
                      <ProtectedRoute>
                        <OrdersList />
                      </ProtectedRoute>
                    } />
                    <Route path="/orders/:orderId" element={
                      <ProtectedRoute>
                        <OrderDetails />
                      </ProtectedRoute>
                    } />
                    {/* Product details - PUBLIC (browsing allowed, add-to-cart/checkout gated) */}
                    <Route path="/product/:id" element={<ProductDetails />} />
                    <Route path="/product-details" element={<ProductDetails />} />
                    {/* Become Seller - PUBLIC (no login required) */}
                    <Route path="/become-seller" element={<BecomeSeller />} />

                    {/* KYC Verification - PROTECTED */}
                    <Route path="/verification" element={
                      <ProtectedRoute>
                        <KYCVerification />
                      </ProtectedRoute>
                    } />
                    <Route path="/kyc-verification" element={
                      <ProtectedRoute>
                        <KYCVerification />
                      </ProtectedRoute>
                    } />

                    {/* Help & Support - PUBLIC */}
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/help-center" element={<HelpCenter />} />

                    {/* Legal Pages - PUBLIC */}
                    <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />

                    {/* Support Chat - PROTECTED */}
                    <Route path="/support" element={
                      <ProtectedRoute>
                        <SupportChat />
                      </ProtectedRoute>
                    } />
                    <Route path="/support-chat" element={
                      <ProtectedRoute>
                        <SupportChat />
                      </ProtectedRoute>
                    } />

                    {/* Disputes - PROTECTED */}
                    <Route path="/disputes" element={
                      <ProtectedRoute>
                        <Disputes />
                      </ProtectedRoute>
                    } />

                    {/* Order Tracking - PROTECTED */}
                    <Route path="/track-order/:orderId" element={
                      <ProtectedRoute>
                        <OrderTracking />
                      </ProtectedRoute>
                    } />

                    <Route path="/category/:id" element={<div>Category Page - Coming Soon</div>} />
                    <Route path="/profile" element={<div>Profile Page - Coming Soon</div>} />

                    {/* 404 Page */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
                
                <Footer />
                <BackToTop />
              </>
            } />
              </Routes>
              </div>
            </Router>
            </OrderProvider>
            </SwapProvider>
          </NotificationProvider>
        </CartProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;